import React, { useEffect, useState } from 'react';
import { eventsApi, getImageUrl } from '../api'; 
import ImageLoader from "./common/ImageLoader";
import './AdminEvents.css'; 

// Helper function to format date from 'YYYY-MM-DD' (date picker output) 
// to 'Month Year' (e.g., "Dec 2024")
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString + 'T00:00:00'); 
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      year: 'numeric' 
    }).format(date);
  } catch (e) {
    console.error("Date formatting error:", e);
    return dateString;
  }
};

// --- Confirmation Modal Component ---
const ConfirmationModal = ({ item, onConfirm, onCancel }) => {
    const [step, setStep] = useState(1);
    const [inputCode, setInputCode] = useState('');
    const REQUIRED_CODE = '8367';
    
    const isYear = item.type === 'year';
    const label = item.name;
    const action = isYear ? 'delete the entire Year & its Events' : 'delete this Event';

    const handleInitialConfirm = () => {
        setStep(2);
    };

    const handleFinalConfirm = () => {
        if (inputCode === REQUIRED_CODE) {
            onConfirm(item.id, isYear);
            onCancel();
        } else {
            alert("❌ Incorrect code. Deletion canceled.");
            setInputCode('');
        }
    };

    return (
        <div className="confirmation-overlay">
            <div className="confirmation-modal">
                <div className="modal-warning-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                </div>
                <h3>Action Confirmation Required</h3>
                {step === 1 && (
                    <>
                        <p className="modal-text">Are you sure you want to {action}: <strong>{label}</strong>?</p>
                        <div className="modal-buttons">
                            <button className="btn-cancel" onClick={onCancel}>No, Keep It</button>
                            <button className="btn-confirm-step1" onClick={handleInitialConfirm}>Yes, Proceed</button>
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <p className="modal-text">To confirm, please enter the security PIN <strong>{REQUIRED_CODE}</strong> below to finalize permanent deletion of: <strong>{label}</strong>.</p>
                        <input
                            type="text"
                            placeholder="Enter 8367"
                            value={inputCode}
                            onChange={(e) => setInputCode(e.target.value)}
                            maxLength="4"
                            className="pin-modal-input"
                            autoFocus
                        />
                        <div className="modal-buttons">
                            <button className="btn-cancel" onClick={onCancel}>Cancel</button>
                            <button className="btn-confirm-step2" onClick={handleFinalConfirm} disabled={inputCode.length !== 4}>
                                Confirm Permanent Delete
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const AdminEvents = () => {
  const [years, setYears] = useState([]);
  const [newYear, setNewYear] = useState('');
  const [selectedYearId, setSelectedYearId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); 
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    eventType: '',
    description: '',
    eventDate: '',
    report: null,
    poster: null
  });

  useEffect(() => {
    fetchYears();
  }, []);

  const apiDeleteYear = async (id) => {
    await eventsApi.deleteYear(id);
    if (selectedYearId === id) setSelectedYearId('');
    fetchYears();
    alert('🗑️ Year and associated events deleted successfully!');
  };

  const apiDeleteEvent = async (id) => {
    await eventsApi.deleteEvent(id);
    fetchYears();
    alert('🗑️ Event deleted successfully!');
  };

  const fetchYears = async () => {
    try {
      const res = await eventsApi.getAll(); 
      setYears(res.data || []);
    } catch (err) {
      console.error('Fetch years error:', err);
    }
  };

  const handleAddYear = async (e) => {
    e.preventDefault();
    if (!newYear.trim()) return;
    try {
      await eventsApi.createYear(newYear.trim());
      setNewYear('');
      fetchYears();
      alert('✅ Year category created!');
    } catch (err) {
      console.error('Add year error:', err);
      alert('❌ Failed to create year.');
    }
  };

  const handleDeleteYearClick = (year) => {
    setDeleteConfirm({ id: year.id, name: year.label, type: 'year' });
  };

  const handleDeleteEventClick = (event) => {
    setDeleteConfirm({ id: event.id, name: event.name, type: 'event' });
  };
  
  const handleFinalDelete = async (id, isYear) => {
    try {
        if (isYear) {
            await apiDeleteYear(id);
        } else {
            await apiDeleteEvent(id);
        }
    } catch (err) {
        console.error("Deletion failed:", err);
        alert("❌ Deletion failed. Check console for details.");
    }
    setDeleteConfirm(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'report' || name === 'poster') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedYearId || !formData.name || !formData.poster || !formData.eventType || !formData.eventDate || !formData.description) {
      return alert("Title, Type, Date, Description, and Poster are required.");
    }

    setLoading(true);
    const displayDate = formatDateForDisplay(formData.eventDate);
    const data = new FormData();
    data.append('name', formData.name);
    data.append('eventType', formData.eventType);
    data.append('description', formData.description);
    data.append('date', displayDate); 
    data.append('poster', formData.poster);
    
    if (formData.report) {
      data.append('report', formData.report);
    }
    data.append('yearId', selectedYearId);

    try {
      await eventsApi.createEvent(data);
      alert('✅ Event added successfully!');
      setFormData({ name: '', eventType: '', description: '', eventDate: '', report: null, poster: null });
      fetchYears();
    } catch (err) {
      console.error('Upload failed:', err);
      alert('❌ Failed to upload event.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-events-wrapper">
      {deleteConfirm && (
        <ConfirmationModal 
          item={deleteConfirm}
          onConfirm={handleFinalDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="fas fa-calendar-alt title-icon"></i>
          <div>
            <h2>Manage Chapter Events</h2>
            <p className="panel-subtitle">Create academic years, upload event reports, posters, and past activities</p>
          </div>
        </div>
      </div>

      {/* Year Management Header Bar */}
      <div className="year-management-bar">
        <span className="bar-label"><i className="fas fa-calendar-plus"></i> Add Event Year</span>
        <form onSubmit={handleAddYear} className="year-form-inline">
          <input
            type="text"
            placeholder="e.g. 2025-2026"
            value={newYear}
            onChange={e => setNewYear(e.target.value)}
            required
          />
          <button type="submit" className="add-year-btn">+ Create Year</button>
        </form>
      </div>

      {/* Event Submission Form */}
      <form className="event-form-card" onSubmit={handleSubmit}>
        <h3 className="sub-heading-tag"><i className="fas fa-plus-circle"></i> Add New Event</h3>

        <div className="event-form-grid">
          <div className="admin-form-group">
            <label>Select Academic Year *</label>
            <select value={selectedYearId} onChange={e => setSelectedYearId(e.target.value)} required>
              <option value="">Select Target Year *</option>
              {years.map(year => (
                <option key={year.id} value={year.id}>{year.label}</option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label>Event Title *</label>
            <input
              type="text"
              name="name"
              placeholder="e.g. National Hackathon 2026"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="admin-form-group">
            <label>Event Type *</label>
            <select
              name="eventType"
              value={formData.eventType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Category *</option>
              <option value="Hackathon">Hackathon</option>
              <option value="Workshop">Workshop</option>
              <option value="Seminar">Seminar</option>
              <option value="Competition">Competition</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label>Event Date *</label>
            <input
              type="date"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="admin-form-group span-2">
            <label>Event Description / Abstract *</label>
            <textarea
              name="description"
              placeholder="Summary of the event, attendees, takeaways..."
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              required
            />
          </div>

          <div className="admin-form-group">
            <label><i className="fas fa-image"></i> Poster Image *</label>
            <input
              type="file"
              name="poster"
              accept="image/*"
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="admin-form-group">
            <label><i className="far fa-file-pdf"></i> Event Report (Optional PDF)</label>
            <input
              type="file"
              name="report"
              accept="application/pdf"
              onChange={handleInputChange}
            />
          </div>
        </div>

        <button type="submit" className="admin-btn-primary submit-event-btn" disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i> Uploading...
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt"></i> Upload &amp; Publish Event
            </>
          )}
        </button>
      </form>

      {/* Event Display Grid by Year */}
      <div className="events-timeline-section">
        {years.map(year => (
          <div key={year.id} className="year-timeline-card">
            <div className="year-card-header">
              <div className="year-title-pill">
                <i className="fas fa-calendar-check"></i>
                <span>{year.label} Events</span>
              </div>
              <button className="delete-year-action-btn" onClick={() => handleDeleteYearClick(year)}>
                <i className="fas fa-trash-alt"></i> Delete Entire Year
              </button>
            </div>
            
            <div className="events-cards-grid">
              {year.Events && year.Events.map(ev => (
                <div key={ev.id} className="event-item-card">
                  <div className="event-poster-box">
                    <ImageLoader src={getImageUrl(ev.posterPath)} alt={ev.name} />
                    <span className="event-type-badge">{ev.eventType}</span>
                  </div>
                  
                  <div className="event-details-body">
                    <span className="event-date-text"><i className="far fa-calendar-alt"></i> {ev.date}</span>
                    <h4>{ev.name}</h4>
                    <p className="event-desc-snippet">{ev.description}</p>
                    
                    <div className="event-card-footer">
                      {ev.reportPath ? (
                        <a href={getImageUrl(ev.reportPath)} target="_blank" rel="noreferrer" className="report-badge-link">
                          <i className="far fa-file-pdf"></i> View PDF
                        </a>
                      ) : (
                        <span className="no-report-pill">No Report</span>
                      )}
                      
                      <button className="delete-ev-icon-btn" onClick={() => handleDeleteEventClick(ev)} title="Delete event">
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {(!year.Events || year.Events.length === 0) && (
                <p className="no-events-subtext">No events uploaded for {year.label} yet.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminEvents;